// What an editor holds and whether it differs from what was loaded, so web
// and mobile ask "Discard your changes?" on exactly the same edits. A field
// counts as changed when it differs at all from what was loaded, a reminder's
// date or time included; changed and changed back is no change.

import { containerOf } from './gtd.ts';
import { taskMoveColumns } from './para.ts';
import type { FilingTarget } from './para.ts';
import type { GtdStatus, Note, Task, TaskStatus } from './types/index.ts';

/** A task editor's fields, as the form holds them: an empty string is "none". */
export interface TaskDraft {
  name: string;
  notes: string;
  due_date: string;
  status: TaskStatus;
  gtd_status: GtdStatus;
  context: string;
  target: FilingTarget | null;
  /** The reminder as a wall time in the user's zone (YYYY-MM-DDTHH:MM), or ''. */
  reminder: string;
}

/** The task as loaded, with its reminder already read in the user's zone. */
export function taskDraft(task: Task, reminder: string): TaskDraft {
  return {
    name: task.name,
    notes: task.notes ?? '',
    due_date: task.due_date ?? '',
    status: task.status,
    gtd_status: task.gtd_status,
    context: task.context ?? '',
    target: containerOf(task),
    reminder,
  };
}

export function taskDraftChanged(saved: TaskDraft, draft: TaskDraft): boolean {
  return (
    saved.name !== draft.name ||
    saved.notes !== draft.notes ||
    saved.due_date !== draft.due_date ||
    saved.status !== draft.status ||
    saved.gtd_status !== draft.gtd_status ||
    saved.context !== draft.context ||
    saved.reminder !== draft.reminder ||
    !sameTarget(saved.target, draft.target)
  );
}

/** Apply a fresh server task to fields the user has not changed locally. */
export function mergeTaskDraft(saved: TaskDraft, draft: TaskDraft, fresh: TaskDraft): TaskDraft {
  const gtdEdited = draft.gtd_status !== saved.gtd_status || draft.context !== saved.context;
  return {
    name: draft.name === saved.name ? fresh.name : draft.name,
    notes: draft.notes === saved.notes ? fresh.notes : draft.notes,
    due_date: draft.due_date === saved.due_date ? fresh.due_date : draft.due_date,
    status: draft.status === saved.status ? fresh.status : draft.status,
    gtd_status: gtdEdited ? draft.gtd_status : fresh.gtd_status,
    context: gtdEdited ? draft.context : fresh.context,
    target: sameTarget(draft.target, saved.target) ? fresh.target : draft.target,
    reminder: draft.reminder === saved.reminder ? fresh.reminder : draft.reminder,
  };
}

/** Only locally changed task fields; coupled status, placement and GTD columns stay together. */
export function taskDraftChanges(saved: TaskDraft, draft: TaskDraft, completedAt = new Date().toISOString()) {
  if (!draft.target || 'newName' in draft.target || draft.target.kind === 'resource') {
    throw new Error('Choose a project or area for this task');
  }
  const gtdEdited = draft.gtd_status !== saved.gtd_status || draft.context !== saved.context;
  const update: Partial<Pick<Task, 'name' | 'due_date' | 'notes' | 'gtd_status' | 'context' | 'status' | 'completed_at' | 'project_id' | 'area_id'>> = {
    ...(draft.name !== saved.name ? { name: draft.name.trim() } : {}),
    ...(draft.due_date !== saved.due_date ? { due_date: draft.due_date || null } : {}),
    ...(draft.notes !== saved.notes ? { notes: draft.notes.trim() || null } : {}),
    ...(gtdEdited ? { gtd_status: draft.gtd_status, context: draft.gtd_status === 'next' ? draft.context || null : null } : {}),
    ...(draft.status !== saved.status ? { status: draft.status, completed_at: draft.status === 'done' ? completedAt : null } : {}),
    ...(!sameTarget(draft.target, saved.target) ? taskMoveColumns(draft.target) : {}),
  };
  return { update, reminder: draft.reminder !== saved.reminder ? draft.reminder || null : undefined };
}

/** A note editor's fields, as the form holds them. */
export interface NoteDraft {
  title: string;
  body: string;
  target: FilingTarget | null;
}

export function noteDraft(note: Note): NoteDraft {
  return { title: note.title, body: note.body ?? '', target: containerOf(note) };
}

export function noteDraftChanged(saved: NoteDraft, draft: NoteDraft): boolean {
  return saved.title !== draft.title || saved.body !== draft.body || !sameTarget(saved.target, draft.target);
}

function sameTarget(a: FilingTarget | null, b: FilingTarget | null): boolean {
  if (a === null || b === null) return a === b;
  if (a.kind !== b.kind) return false;
  if ('id' in a && 'id' in b) return a.id === b.id;
  if ('newName' in a && 'newName' in b) return a.newName === b.newName;
  return false;
}
